import type { Me } from '@shared/types/factory';
import { useAuthStore } from '@/stores/authStore';
import { redirectToLoginForExpiredSession } from '@/lib/auth-redirect';
import { currentProjectHeader } from '@/lib/current-project';
import { whenSessionReady } from '@/lib/session-ready';
import {
  NETWORK_ERROR_TEXT,
  isSessionGone,
  noResponse,
  toApiResponse,
  type ApiResponse,
} from '@/lib/api-contract';

export type { ApiResponse } from '@/lib/api-contract';

/** The header project-scoped routes read: a project id or slug (docs/first-slice-plan.md §2). */
export const PROJECT_HEADER = 'X-Project-Id';

/** /api/auth/session, /api/auth/refresh and accepting an invite: the factory JWT and the user. */
export interface AuthResponse {
  token: string;
  pending_invite_token?: string | null;
  user: Me;
}

const isDev = () => process.env.NODE_ENV === 'development';

/** A 401 from these is the answer itself, never a reason to renew the session. */
const AUTH_BOOTSTRAP_PATHS = ['/auth/session', '/auth/refresh'];

/**
 * Every API call goes through here. It never throws: failures come back with `status` (0 when no
 * answer arrived), `code` and a readable `error`. An expired token is renewed once, shared by every
 * request that hit it, and the request is retried once. Only /auth/refresh answering 401 or 403
 * signs the person out; a network or server failure keeps them signed in.
 */
export async function fetchApi<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return request<T>(path, withProjectHeader(options), false);
}

/**
 * Inside a project route, name the project (lib/current-project.ts). A header the caller passes
 * wins (a page or dialog naming its project explicitly). Resolved once, before the first send, so
 * a retry after renewing the session goes to the same project even if the tab has moved on.
 */
function withProjectHeader(options: RequestInit): RequestInit {
  const headers = (options.headers ?? {}) as Record<string, string>;
  if (Object.keys(headers).some((name) => name.toLowerCase() === PROJECT_HEADER.toLowerCase())) return options;
  const project = currentProjectHeader();
  if (!project) return options;
  return { ...options, headers: { ...headers, [PROJECT_HEADER]: project } };
}

async function request<T>(path: string, options: RequestInit, retried: boolean): Promise<ApiResponse<T>> {
  // The app draws before the page-load session check answers (T-68): wait for it, except the check itself.
  const bootstrap = AUTH_BOOTSTRAP_PATHS.some((p) => path.startsWith(p));
  const verdict = bootstrap ? 'send' : await whenSessionReady(options.signal);
  if (verdict === 'drop') return noResponse<T>(new DOMException('Aborted', 'AbortError'));
  const { res, sentToken } = await sendOnce<T>(path, options);
  // Made for a remembered person the check signed out: a 401 is the answer, the app is going to /login.
  if (verdict === 'signed-out') return res;
  if (res.status === 401 && !retried && !AUTH_BOOTSTRAP_PATHS.some((p) => path.startsWith(p))) {
    return renewAndRetry<T>(path, options, sentToken);
  }
  return res;
}

/** One request and its answer. Records the bearer actually sent, so a 401 knows what went stale. */
async function sendOnce<T>(
  path: string,
  options: RequestInit
): Promise<{ res: ApiResponse<T>; sentToken: string | null }> {
  const method = options.method ?? 'GET';
  // FormData gets no Content-Type, so the browser sets the multipart boundary.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string> | undefined),
  };
  if (isFormData) {
    delete headers['Content-Type'];
    delete headers['content-type'];
  }
  // A caller's own Authorization is kept; otherwise this tab's token.
  const storeToken = useAuthStore.getState().user?.token;
  if (!headers.Authorization && !headers.authorization && storeToken) {
    headers.Authorization = `Bearer ${storeToken}`;
  }
  const bearer = headers.Authorization ?? headers.authorization;
  const sentToken = bearer?.startsWith('Bearer ') ? bearer.slice('Bearer '.length) : null;

  let res: ApiResponse<T>;
  try {
    // eslint-disable-next-line no-restricted-syntax -- fetchApi itself.
    const response = await fetch(`/api${path}`, {
      ...options,
      credentials: options.credentials ?? 'include',
      headers,
    });
    const text = await response.text();
    res = toApiResponse<T>(response.status, text);
    if (isDev() && text && res.data === undefined && res.body === undefined) {
      console.warn(`[API] ${method} ${path} answered with something other than JSON:`, text.slice(0, 200));
    }
  } catch (e) {
    res = noResponse<T>(e);
  }
  logResult(method, path, res);
  return { res, sentToken };
}

/** Dev: one line per request. Production: only failures with no answer or a server error. */
function logResult(method: string, path: string, res: ApiResponse<unknown>) {
  if (isDev()) {
    console.log(`[API] ${method} ${path} → ${res.status}${res.code ? ` ${res.code}` : ''}`);
  } else if (res.status === 0 || res.status >= 500) {
    // No body, headers or query string: they can carry tokens and personal data.
    console.warn(`[API] ${method} ${path.split('?')[0]} → ${res.status}${res.code ? ` ${res.code}` : ''}`);
  }
}

function sessionEnded<T>(): ApiResponse<T> {
  redirectToLoginForExpiredSession();
  return { status: 401, code: 'SESSION_EXPIRED', error: 'Your session has ended. Sign in again.' };
}

/** A 401 on a first attempt: renew the session (or use one another request renewed) and retry once. */
async function renewAndRetry<T>(
  path: string,
  options: RequestInit,
  sentToken: string | null
): Promise<ApiResponse<T>> {
  const storeToken = useAuthStore.getState().user?.token;
  if (!storeToken) return sessionEnded();

  let token = storeToken;
  if (storeToken === sentToken) {
    const outcome = await refreshSessionOnce(storeToken);
    if (outcome.kind === 'signed-out') return sessionEnded();
    if (outcome.kind === 'failed') {
      useAuthStore.getState().setSessionRefreshFailed(true);
      if (outcome.status === 0) return { status: 0, code: 'NETWORK', error: NETWORK_ERROR_TEXT };
      return {
        status: outcome.status,
        code: 'SESSION_REFRESH_FAILED',
        error: "The factory couldn't renew your session. Try again in a moment.",
      };
    }
    token = outcome.token;
  }
  if (options.signal?.aborted) return noResponse<T>(new DOMException('Aborted', 'AbortError'));

  const headers = { ...(options.headers as Record<string, string> | undefined) };
  delete headers.authorization;
  headers.Authorization = `Bearer ${token}`;
  return request<T>(path, { ...options, headers }, true);
}

type RefreshOutcome =
  | { kind: 'ok'; token: string }
  | { kind: 'signed-out'; status: number }
  | { kind: 'failed'; status: number };

/** One renewal per tab at a time: every request that hit the same stale token waits for it. */
let refreshInFlight: { from: string; promise: Promise<RefreshOutcome> } | null = null;

function refreshSessionOnce(stale: string): Promise<RefreshOutcome> {
  if (refreshInFlight?.from === stale) return refreshInFlight.promise;
  const entry = { from: stale, promise: runRefresh(stale) };
  refreshInFlight = entry;
  void entry.promise.finally(() => {
    if (refreshInFlight === entry) refreshInFlight = null;
  });
  return entry.promise;
}

async function runRefresh(stale: string): Promise<RefreshOutcome> {
  const res = await refreshToken(stale);
  if (res.data?.token) {
    const store = useAuthStore.getState();
    // A sign-in made while this ran wins: keep its token, not the renewed one.
    if (store.user?.token === stale) store.updateUserAndToken(res.data);
    const current = useAuthStore.getState().user?.token;
    return current ? { kind: 'ok', token: current } : { kind: 'signed-out', status: 401 };
  }
  if (isSessionGone(res.status)) return { kind: 'signed-out', status: res.status };
  // A 2xx with no token is a broken answer, not a signed-out person.
  return { kind: 'failed', status: res.status >= 200 && res.status < 300 ? 502 : res.status };
}

/** Swap this tab's token for a fresh one. Only fetchApi calls it: a 401 means renew, then retry. */
async function refreshToken(token: string) {
  return fetchApi<AuthResponse>('/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ token }),
    credentials: 'include',
  });
}

/** Retry after a failed renewal (the "Can't reach the factory" banner). Signs out only on 401 or 403. */
export async function retrySessionRefresh(): Promise<RefreshOutcome['kind']> {
  const token = useAuthStore.getState().user?.token;
  if (!token) {
    redirectToLoginForExpiredSession();
    return 'signed-out';
  }
  const outcome = await refreshSessionOnce(token);
  if (outcome.kind === 'signed-out') redirectToLoginForExpiredSession();
  // updateUserAndToken clears the flag, unless a newer sign-in kept its own token meanwhile.
  if (outcome.kind === 'ok') useAuthStore.getState().setSessionRefreshFailed(false);
  return outcome.kind;
}

/** Exchange a BetterAuth session cookie for the app JWT + user payload (same-origin cookies). */
export async function exchangeBetterAuthSession() {
  return fetchApi<AuthResponse>('/auth/session', { method: 'POST', body: JSON.stringify({}) });
}
